import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, dateBr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, LogOut, MessageCircle, CreditCard, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";
import PaymentSetup from "@/components/billing/PaymentSetup";

const TERMS_URL = "https://oplyx.com.br/termos";

// Tela de bloqueio. Quem pode resolver pagando (teste antigo vencido, assinatura
// vencida, cancelamento por inadimplência) paga daqui mesmo: aceite dos termos
// + checkout Asaas. Antes, sem termos aceitos o "Renovar" falhava, e o 402
// mandava para /plans, que trocava o plano sem cobrar e deixava o cliente preso.
function ExpiredScreen({ info }) {
  const nav = useNavigate();
  const [whatsapp, setWhatsapp] = useState("5548991207631");
  const [starting, setStarting] = useState(false);
  const [billing, setBilling] = useState(null);
  const [termsChecked, setTermsChecked] = useState(false);

  const canPay = !!info?.can_pay;
  const isOwner = (() => {
    try { return JSON.parse(localStorage.getItem("oplyx_user") || "{}").role === "owner"; }
    catch { return false; }
  })();
  const isTrialEnd = info?.tenant_state === "demo";

  useEffect(() => {
    fetch("/api/public/platform-config")
      .then(r => r.json())
      .then(d => { if (d.support_whatsapp) setWhatsapp(d.support_whatsapp); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!canPay || !isOwner) return;
    api.get("/billing/me").then(r => setBilling(r.data)).catch(() => setBilling({}));
  }, [canPay, isOwner]);

  const logout = () => { localStorage.clear(); nav("/auth"); };

  const needsTerms = billing && !billing.terms_accepted_at;

  const pay = async () => {
    if (needsTerms && !termsChecked) {
      toast.error("Aceite os Termos de Uso para continuar.");
      return;
    }
    setStarting(true);
    try {
      if (needsTerms) await api.post("/billing/accept-terms", { accepted: true });
      const { data } = await api.post("/billing/start-checkout", {
        cycle: "monthly", billing_type: "UNDEFINED",
      });
      if (data.invoice_url) {
        window.location.href = data.invoice_url;
      } else {
        toast.error("Link de pagamento ainda não disponível. Fale com o suporte.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível gerar o link. Fale com o suporte via WhatsApp.");
    } finally {
      setStarting(false);
    }
  };

  const waUrl = `https://wa.me/${whatsapp}?text=${encodeURIComponent(
    canPay ? "Olá! Quero ativar minha assinatura do Oplyx." : "Olá! Preciso de ajuda com minha conta Oplyx."
  )}`;

  const title = isTrialEnd ? "Ative sua assinatura"
    : canPay ? "Assinatura expirada"
    : "Acesso bloqueado";

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col">
      <header className="px-6 py-5 flex items-center justify-between border-b border-neutral-200">
        <div className="flex items-center gap-2 font-bold text-neutral-900">
          <InfinityIcon className="w-5 h-5 text-[#00FF88]" /> OPLYX
        </div>
        <Button variant="ghost" size="sm" onClick={logout} className="text-neutral-500">
          <LogOut className="w-4 h-4 mr-1" /> Sair
        </Button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-6 py-16">
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
          <CreditCard className="w-7 h-7 text-amber-600" />
        </div>

        <div className="space-y-2 max-w-sm">
          <h1 className="text-2xl font-bold text-neutral-900" data-testid="blocked-title">{title}</h1>
          <p className="text-neutral-500 text-sm leading-relaxed" data-testid="blocked-reason">
            {canPay
              ? "Ative sua assinatura para continuar usando o Oplyx. Seus dados estão salvos e seguros."
              : (info?.blocked_reason || "Entre em contato com a OPLYX para regularizar.")}
          </p>
        </div>

        {canPay && isOwner && needsTerms && (
          <label className="flex items-start gap-3 max-w-sm text-left text-sm text-neutral-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={termsChecked}
              onChange={(e) => setTermsChecked(e.target.checked)}
              className="mt-0.5 shrink-0"
              data-testid="blocked-terms-checkbox"
            />
            <span>
              Li e aceito os{" "}
              <a href={TERMS_URL} target="_blank" rel="noreferrer" className="underline text-emerald-700 font-medium">
                Termos de Uso e Contrato de Licença
              </a>{" "}
              da OPLYX.
            </span>
          </label>
        )}

        {canPay && !isOwner && (
          <p className="text-sm text-neutral-600 max-w-sm" data-testid="blocked-not-owner">
            Peça ao responsável pela conta para entrar e ativar a assinatura.
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
          {canPay && isOwner && (
            <Button
              onClick={pay}
              disabled={starting || !billing || (needsTerms && !termsChecked)}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-full h-11"
              data-testid="blocked-pay"
            >
              {starting
                ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                : <CreditCard className="w-4 h-4 mr-2" />}
              {isTrialEnd ? "Ativar assinatura" : "Renovar agora"}
            </Button>
          )}
          <Button
            asChild
            variant="outline"
            className="flex-1 rounded-full h-11 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
          >
            <a href={waUrl} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="w-4 h-4 mr-2" />
              Falar com suporte
            </a>
          </Button>
        </div>

        {canPay && (
          <p className="text-xs text-neutral-400">
            O pagamento é processado via Pix, boleto ou cartão pelo ASAAS.<br />
            Após confirmação o acesso é liberado automaticamente.
          </p>
        )}
      </main>
    </div>
  );
}

// Cadastro sem forma de pagamento (contratação sem trial): a conta existe mas
// só libera depois do passo de pagamento. Quem fechou a aba no meio cai aqui.
function PaymentSetupScreen({ onActivated }) {
  const nav = useNavigate();
  const logout = () => { localStorage.clear(); nav("/auth"); };
  return (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col" data-testid="payment-setup-screen">
      <header className="px-6 py-5 flex items-center justify-between border-b border-neutral-200">
        <div className="flex items-center gap-2 font-bold text-neutral-900">
          <InfinityIcon className="w-5 h-5 text-[#00FF88]" /> OPLYX
        </div>
        <Button variant="ghost" size="sm" onClick={logout} className="text-neutral-500">
          <LogOut className="w-4 h-4 mr-1" /> Sair
        </Button>
      </header>
      <main className="flex-1 flex justify-center px-4 py-10">
        <div className="w-full max-w-md space-y-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Falta a forma de pagamento</h1>
            <p className="text-sm text-neutral-500 mt-1">Cadastre como prefere pagar para liberar o acesso à OPLYX.</p>
          </div>
          <PaymentSetup onActivated={onActivated} />
        </div>
      </main>
    </div>
  );
}

// Conta encerrada: durante 90 dias depois do fim do acesso o dono ainda baixa os
// dados da empresa e os XMLs fiscais (Termos, cláusula 4ª). Nada operacional.
function ExportOnlyScreen({ info, onReactivate }) {
  const [busy, setBusy] = useState("");
  const isOwner = (() => {
    try { return (JSON.parse(localStorage.getItem("oplyx_user") || "{}").role || "owner") === "owner"; }
    catch { return false; }
  })();
  const baixar = async (kind) => {
    setBusy(kind);
    try {
      const url = kind === "dados" ? "/export/my-data" : "/fiscal/export-xml";
      const { data } = await api.get(url, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = kind === "dados" ? "oplyx-dados.json" : "oplyx-xmls-fiscais.zip";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error("Não foi possível baixar agora. Tente de novo ou fale com a OPLYX.");
    } finally { setBusy(""); }
  };
  const logout = () => { localStorage.clear(); window.location.href = "/auth"; };
  return (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col" data-testid="export-only-screen">
      <header className="px-6 py-5 flex items-center justify-between border-b border-neutral-200">
        <div className="flex items-center gap-2 font-bold text-neutral-900">
          <InfinityIcon className="w-5 h-5 text-[#00FF88]" /> OPLYX
        </div>
        <Button variant="ghost" size="sm" onClick={logout} className="text-neutral-600">
          <LogOut className="w-4 h-4 mr-1" /> Sair
        </Button>
      </header>
      <main className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-5 py-16">
        <h1 className="text-2xl font-bold text-neutral-900">O contrato foi encerrado</h1>
        <p className="text-neutral-700 text-sm leading-relaxed max-w-md">
          Até <strong>{dateBr(info.export_until)}</strong> você pode baixar os dados da empresa e os XMLs das notas
          fiscais. Depois dessa data, o acesso para exportação será encerrado. Os dados sujeitos à eliminação serão
          excluídos ou anonimizados em até 30 dias, preservadas as informações cuja conservação seja legalmente exigida
          ou permitida.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          {isOwner && (
            <Button onClick={() => baixar("dados")} disabled={!!busy} className="rounded-full" data-testid="export-only-data">
              {busy === "dados" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Baixar dados da empresa
            </Button>
          )}
          <Button variant="outline" onClick={() => baixar("xml")} disabled={!!busy} className="rounded-full" data-testid="export-only-xml">
            {busy === "xml" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Baixar XMLs fiscais
          </Button>
        </div>
        {!isOwner && <p className="text-xs text-neutral-600">Os dados completos da empresa só o dono da conta baixa.</p>}
        {info.can_pay && (
          <button type="button" onClick={onReactivate} className="text-sm text-emerald-800 underline">Quero reativar a assinatura</button>
        )}
      </main>
    </div>
  );
}

export default function SubscriptionGate({ children }) {
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const [reactivating, setReactivating] = useState(false);

  // `active` do backend é o mesmo veredito do gate de acesso (_check_sub).
  const check = useCallback(() => (
    api.get("/subscription").then(r => {
      setInfo(r.data);
      setState(r.data.active ? "ok" : "blocked");
    }).catch(() => setState((s) => (s === "loading" ? "ok" : s)))
  ), []);

  useEffect(() => { check(); }, [check]);

  // 402 no meio do uso (o acesso caiu com o app aberto): reconsulta em vez de
  // presumir bloqueio — 402 também é usado por limite de plano (ex.: filiais).
  useEffect(() => {
    const id = api.interceptors.response.use(r => r, e => {
      if (e?.response?.status === 402 && state !== "blocked") check();
      return Promise.reject(e);
    });
    return () => api.interceptors.response.eject(id);
  }, [check, state]);

  if (state === "loading") return null;
  if (state === "blocked" && info?.tenant_state === "awaiting_payment") {
    return <PaymentSetupScreen onActivated={() => { setState("loading"); check(); }} />;
  }
  const exportWindow = state === "blocked" && ["cancelled", "canceled"].includes(info?.tenant_state)
    && info?.export_until && info.export_until >= new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
  if (exportWindow && !reactivating) {
    return <ExportOnlyScreen info={info} onReactivate={() => setReactivating(true)} />;
  }
  if (state === "blocked") return <ExpiredScreen info={info} />;
  return children;
}
